import React, { useState } from 'react'
import { Dialog } from '@progress/kendo-react-dialogs'

import GRADUATION_ICON from '@public/assets/graduation.svg'
import PREVIOUS_ICON from '@public/assets/button/arrow-chevron-left.svg'
import NEXT_ICON from '@public/assets/button/arrow-chevron-right_white.svg'

import { cn } from '@/utils/mergeClassNames'

import { TUTORIAL_DIALOG_TITLE, TUTORIAL_STEPS } from '../constants'
import { useTutorialVideo } from '../hooks/useTutorialVideo'
import type { TutorialDialogProps } from '../types'
import { getTutorialDialogSize } from '../utils/tutorialDialog'
import { TutorialStepContent } from './TutorialStepContent'

export function TutorialDialog({ onClose }: TutorialDialogProps) {
  const [activeIndex, setActiveIndex] = useState(0)
  const videoRef = useTutorialVideo(activeIndex)

  const activeStep = TUTORIAL_STEPS[activeIndex]
  const isFirstStep = activeIndex === 0
  const isLastStep = activeIndex === TUTORIAL_STEPS.length - 1
  const dialogSize = getTutorialDialogSize()

  const handlePrevious = () => {
    setActiveIndex(currentIndex => Math.max(currentIndex - 1, 0))
  }

  const handleNext = () => {
    setActiveIndex(currentIndex =>
      Math.min(currentIndex + 1, TUTORIAL_STEPS.length - 1)
    )
  }

  return (
    <Dialog
      width={dialogSize.width}
      height={dialogSize.height}
      onClose={onClose}
      className="!z-[1000001] [&_.k-window-content]:overflow-hidden [&_.k-window]:rounded-3xl"
    >
      <header className="flex items-center justify-between border-b border-[#d9d9d9] px-[30px] py-[25px]">
        <div className="flex items-center gap-3">
          <img className="size-4" src={GRADUATION_ICON} alt="" />
          <h1 className="m-0 text-lg font-semibold text-[#141414]">
            {TUTORIAL_DIALOG_TITLE}
          </h1>
        </div>
        <button
          type="button"
          className="flex h-12 w-12 cursor-pointer items-center justify-center rounded border-0 bg-transparent text-2xl text-[#333] hover:bg-neutral-100"
          aria-label="Close tutorial"
          onClick={onClose}
        >
          ×
        </button>
      </header>

      <div className="flex h-[calc(100%-78px)] w-full">
        <nav
          className="w-[280px] shrink-0 overflow-y-auto border-r border-[#d9d9d9] py-[30px]"
          aria-label="Tutorial steps"
        >
          {TUTORIAL_STEPS.map((step, index) => {
            const isActive = activeIndex === index
            const isCompleted = index < activeIndex

            return (
              <button
                key={step.id}
                type="button"
                className={cn(
                  'flex min-h-[54px] w-full cursor-pointer items-start gap-[10px] border-0 py-4 pl-4 pr-4 text-left text-sm tracking-[0.25px] transition-colors',
                  isActive
                    ? 'bg-[#333] font-semibold text-white'
                    : 'bg-white hover:bg-neutral-100',
                  !isActive && (isCompleted ? 'text-[#333333]' : 'text-[#666]')
                )}
                aria-current={isActive ? 'step' : undefined}
                onClick={() => setActiveIndex(index)}
              >
                <span
                  className={cn(
                    'mt-1.5 size-2 shrink-0 rounded-full',
                    isActive
                      ? 'bg-white'
                      : isCompleted
                        ? 'bg-[#4CAF50]'
                        : 'bg-[#d9d9d9]'
                  )}
                />
                <span>{step.title}</span>
              </button>
            )
          })}
        </nav>

        <main className="flex flex-1 p-[30px]">
          <div className="flex w-[calc(100%-140px)] shrink-0 flex-col gap-5 overflow-y-auto">
            <section>
              <h2 className="mb-3 mt-0 text-lg font-semibold text-black">
                {activeStep.contentTitle || activeStep.title}
              </h2>
              <TutorialStepContent stepId={activeStep.id} />
            </section>

            <video
              ref={videoRef}
              className="aspect-video h-auto w-full rounded-[20px] border border-[#d9d9d9] object-contain shadow-[0_1px_2px_0_rgba(0,0,0,0.3),0_2px_6px_2px_rgba(0,0,0,0.15)]"
              controls
              autoPlay
            >
              <source src={activeStep.videoUrl} type="video/mp4" />
            </video>
          </div>

          <footer className="flex h-full w-[140px] shrink-0 items-end justify-end gap-2">
            {!isFirstStep && (
              <button
                type="button"
                className="flex size-11 cursor-pointer items-center justify-center rounded-[40px] border border-[#dadada] bg-white px-3 hover:bg-[#e2e1e3]"
                aria-label="Previous tutorial step"
                onClick={handlePrevious}
              >
                <img src={PREVIOUS_ICON} alt="" />
              </button>
            )}

            {isLastStep ? (
              <button
                type="button"
                className="flex h-11 cursor-pointer items-center justify-center rounded-[40px] border-0 bg-[#333333] px-3 text-white hover:bg-black"
                onClick={onClose}
              >
                Close
              </button>
            ) : (
              <button
                type="button"
                className="flex size-11 cursor-pointer items-center justify-center rounded-[40px] border-0 bg-[#333333] px-3 hover:bg-black"
                aria-label="Next tutorial step"
                onClick={handleNext}
              >
                <img src={NEXT_ICON} alt="" />
              </button>
            )}
          </footer>
        </main>
      </div>
    </Dialog>
  )
}
